import { getUserNavigationMenu, getUserPermission, getUserPreference } from '@app/api/userService';
import { getFeatures } from '@app/api/commonService';
import { SecureRoute, useOktaAuth } from '@okta/okta-react';
import { Skeleton } from 'antd';
import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@app/redux/reducers/state';
import CommonLayout from '@app/layouts/CommonLayout';
import { LOGIN_SUCCESS, SET_TENANT } from '@app/redux/actionTypes/authActionTypes';
import AppError from '@app/components/AppError';
import logger from '@app/utils/logger';
import { setUserLoaded } from '@app/redux/actions/authActions';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import config, { getApiUrl } from '@app/utils/config';
import { errorNotification, successNotification } from '@app/utils/antNotifications';
import { updateGridHasUpdates } from '@app/redux/actions/gridsActions';
import { setFeatures, setVirusScanStatus } from '@app/redux/actions/configurationActions';
import Constants from '@app/constants';
import TenantSelection from '../components/TenatSelection';
import { setUserPermission, setUserPreference } from '@app/redux/actions/userManagementActions';
import { ServiceType } from '@app/types/ServiceType';
import { addRumJourneyAction } from '@app/utils/datadogRum';
import { clearJourneyId, getJourneyId, startJourney, trackJourneyStep } from '@app/utils/telemetry';
const w = window as any;

const RoutesWithSubRoutes = (route: any) => {
  const dispatch = useDispatch();
  const { authState, oktaAuth } = useOktaAuth();
  const [apiError, setApiError] = useState<any>({ message: '', statusCode: '' });
  const [connection, setConnection] = useState<null | HubConnection>(null);
  const [connectionAttempts, setConnectionAttempts] = useState<number>(0);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const { tenant, user, menuItems } = useSelector((state: RootState) => state.auth);

  const MAX_RECONNECTION_ATTEMPTS = 5;
  const CONNECTION_TIMEOUT = 30000; // 30 seconds

  const fetchUserData = async () => {
    try {
      const permissions = await getUserPermission();
      const preferences = await getUserPreference();
      dispatch(setUserPermission(permissions));
      if (preferences == null || preferences === '') {
        dispatch(setUserPreference(w._env_ && w._env_.REACT_APP_DEFAULT_CULTURE_CODE));
      } else {
        dispatch(setUserPreference(preferences));
      }
    } catch (e) {
      logger.error('RoutesWithSubRoutes', 'fetchUserData Error', e);
      if (e.statusCode !== 403) setApiError({ message: 'ERR_NETWORK' });
      else setApiError(e);
    }
  };

  const fetchFeatures = async () => {
    try {
      const features = await getFeatures();
      dispatch(setFeatures(features));
    } catch (e) {
      logger.error('RoutesWithSubRoutes', 'fetchFeatures Error', e);
      // Set default features on error
      dispatch(
        setFeatures({
          fileEditing: false,
          contactsInSendEmail: false,
          formConfigurations: false,
          aiTagging: false,
          bulkDocumentCreation: false,
          internalAIAssistant: false,
          docuSignSigning: false,
          manualRouting: false,
        })
      );
    }
  };

  const fetchVirusScanStatus = async () => {
    // TODO: Uncomment when API is ready
    // try {
    //   const enabled = await getVirusScanStatus();
    //   dispatch(setVirusScanStatus(enabled));
    // } catch (e) {
    //   logger.error('RoutesWithSubRoutes', 'fetchVirusScanStatus Error', e);
    //   dispatch(setVirusScanStatus(false));
    // }

    // Temporary hardcoded value - replace with API call above
    dispatch(setVirusScanStatus(true));
  };

  useEffect(() => {
    if (connection && !isConnecting) {
      connection.on('ReceiveMessage', (subject, message) => {
        if (subject % 2 === 0) {
          errorNotification([''], message);
        } else {
          successNotification([''], message);
        }
        dispatch(updateGridHasUpdates(true));
      });

      // Handle connection closed event
      connection.onclose((error) => {
        if (error) {
          logger.error('YJ Generic', 'Push Notification Connection Closed', error);
        }
        setIsConnecting(false);
      });

      // Handle reconnecting event
      connection.onreconnecting((error) => {
        logger.warn('YJ Generic', 'Push Notification Reconnecting', error);
        setConnectionAttempts((prev) => prev + 1);
      });

      // Handle reconnected event
      connection.onreconnected(() => {
        logger.info('YJ Generic', 'Push Notification', 'Reconnected successfully');
        setConnectionAttempts(0);
      });

      setIsConnecting(true);

      const startConnection = async () => {
        try {
          await connection.start();
          logger.info('YJ Generic', 'Push Notification', 'started push notification');
          setConnectionAttempts(0);
          setIsConnecting(false);
        } catch (error) {
          logger.error('YJ Generic', 'Push Notification Start Failed', error);
          setIsConnecting(false);

          // Don't retry if max attempts exceeded
          if (connectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
            logger.error('YJ Generic', 'Push Notification', 'Max reconnection attempts reached. Stopping reconnection.');
            errorNotification([''], 'Failed to connect to notification service. Please refresh the page.');
          }
        }
      };

      startConnection();

      // Cleanup function
      return () => {
        if (connection && connection.state === HubConnectionState.Connected) {
          connection.stop();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, connectionAttempts]);

  useEffect(() => {
    let tids = (authState?.accessToken?.claims.tid as Array<string>) ?? [];

    if (tids.length === 1 && !tenant) dispatch({ type: SET_TENANT, payload: tids[0] });

    if (authState?.isAuthenticated && tenant) {
      if (!tids.find((e) => e === tenant)) dispatch({ type: SET_TENANT, payload: null });

      if (menuItems.length === 0) {
        if (connection != null && connection.state === HubConnectionState.Connected) {
          connection.stop();
        }
        if (authState?.isAuthenticated) {
          oktaAuth.getUser().then((user) => {
            dispatch(setUserLoaded(true, user));
            sessionStorage.setItem('loggedUserId', `${user?.preferred_username}`);
            sessionStorage.setItem('systemCode', tenant);
          });

          // Custom retry policy with exponential backoff and max attempts
          const retryDelays = [0, 2000, 5000, 10000, 30000]; // 0s, 2s, 5s, 10s, 30s

          const connect = new HubConnectionBuilder()
            .withUrl(
              getApiUrl(
                config.api[ServiceType.Notification].pushNotification.slice(
                  config.api[ServiceType.Notification].pushNotification.indexOf('api')
                ),
                ServiceType.Notification
              ),
              {
                accessTokenFactory: () => authState?.accessToken?.accessToken ?? '',
                timeout: CONNECTION_TIMEOUT,
              }
            )
            .withAutomaticReconnect({
              nextRetryDelayInMilliseconds: (retryContext) => {
                // Stop reconnecting after MAX_RECONNECTION_ATTEMPTS
                if (retryContext.previousRetryCount >= MAX_RECONNECTION_ATTEMPTS) {
                  logger.error('YJ Generic', 'Push Notification', `Max reconnection attempts (${MAX_RECONNECTION_ATTEMPTS}) reached`);
                  return null; // Stop reconnecting
                }

                // Use exponential backoff
                const delay = retryDelays[retryContext.previousRetryCount] || 30000;
                logger.info('YJ Generic', 'Push Notification', `Retry attempt ${retryContext.previousRetryCount + 1} after ${delay}ms`);
                return delay;
              }
            })
            .build();
          setConnection(connect);
        }
        getUserNavigationMenu()
          .then(async (response) => {

            dispatch({
              type: LOGIN_SUCCESS,
              payload: {
                access_token: oktaAuth.getAccessToken(),
                menuItems: response.data,
              },
            });
          })
          .catch((e) => {
            if (e.statusCode && e.message) {
              setApiError(e);
            } else {
              setApiError({ message: 'ERR_NETWORK' });
            }
          });

        fetchUserData();
        fetchFeatures();
        fetchVirusScanStatus();
      }
    } else if (!authState?.isAuthenticated) {
      clearJourneyId('login_session_ready');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.isAuthenticated, tenant]);

  const getApiErrorMessage = (error: any, user: any) => {
    if (error.message === 'ERR_NETWORK') {
      return 'A network error occurred. Please check your connection.';
    } else if (error.statusCode === 403) {
      return `You do not have permission to access DMS with your current login. Please contact your Organization's administrator for assistance.`;
    } else if (error.message !== '') {
      return error.message;
    } else {
      return 'An unexpected error occurred.';
    }
  };

  const renderSecureContent = (props: any) => {
    if (!tenant && authState?.isAuthenticated) {
      return (
        <CommonLayout>
          <TenantSelection />
        </CommonLayout>
      );
    }

    if (menuItems.length > 0 || !authState?.isAuthenticated) {
      const loginJourneyId = getJourneyId('login_session_ready');
      if (loginJourneyId && authState?.isAuthenticated && tenant) {
        trackJourneyStep(
          'journey_complete',
          {
            journey_id: loginJourneyId,
            journey_name: 'login_session_ready',
            step_name: 'home_loaded',
            step_order: 4,
            step_status: 'success',
          },
          addRumJourneyAction
        );
        clearJourneyId('login_session_ready');
      }

      if (authState?.isAuthenticated && tenant && !getJourneyId('first_value_activation')) {
        startJourney(
          'first_value_activation',
          'file_area_reached',
          {
            entry_point: 'main_routes',
          },
          addRumJourneyAction
        );
      }

      return (
        <Suspense fallback={<Skeleton />}>
          <route.component {...props} title={route.title} routes={route.routes} />
        </Suspense>
      );
    }

    return apiError.message === '' ? (
      <Skeleton />
    ) : (
      <CommonLayout>
        <AppError
          message={getApiErrorMessage(apiError, user)}
          showContactAdmin={false}
          showLogoutButton={false}
          headerMessage={apiError.statusCode === 403 ? 'Access Denied' : 'Error!'}
        />
      </CommonLayout>
    );
  };

  return (
    <SecureRoute
      path={route.path}
      render={(props) => renderSecureContent(props)}
    />
  );
};

export default RoutesWithSubRoutes;
